import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ChevronIcon from "../../components/icons/ChevronIcon";
import { useModal } from '../../contexts/ModalContext';
import { programsApi } from "../../services/api/program.api";
import { userCoursesApi } from "../../services/api/user-courses.api";
import { userExamsApi } from "../../services/api/user-exams.api";
import { AddableCourse, CourseLite, CourseOption } from "../../types/course.response";
import { MutationEnvelope } from "../../types/mutation.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { useCourseActions } from "../../hooks/useCourseActions";
import { usePinnedSidebar } from "../../hooks/usePinnedSidebar";
import { programSlices } from "../../services/programSlices";
import { reconcileUserItems } from "../../services/userCourseCache";
import { ensureSliceCacheIdentity } from "../../services/sliceCacheIdentity";
import { getBroadKind } from "../../utils/requirementResponseUtils";
import axios from "axios";
import RequirementPageSkeleton from "../../components/loading/RequirementPageSkeleton";
import RequirementDetailSection from "../../components/requirement-detail/RequirementDetail";
import RequirementInfoSidebar from "./RequirementInfoSidebar";
import Attention from "../../components/icons/Attention";

interface RequirementProps {
    overlayProgramId?: string;
    overlayRequirementId?: string;
    onClose?: () => void;
    onDataChanged?: () => void;
    scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
    initialUserCourses?: CourseOption[];
}

/**
 * Requirement 详情页(独立路由 + overlay 两用),数据全部来自切片编排器:
 * skeleton 提供 requirement 树与节点 summaries,该 requirement 的切片提供课程摘要,
 * 重展示数据由 CourseSetRows 经 /courses/batch 补水。缓存命中时打开零网络请求;
 * overlay 场景下 Home/Program 已加载的数据按原粒度复用(skeleton 已到 → 结构即时,
 * 切片已到 → 课程即时)。
 */
export default function Requirement({ overlayProgramId, overlayRequirementId, onClose, onDataChanged, scrollContainerRef, initialUserCourses }: RequirementProps) {
    const isOverlay = !!onClose;

    const { programId: routeProgramId, requirementId: routeRequirementId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const programId = overlayProgramId ?? routeProgramId;
    const requirementId = overlayRequirementId ?? routeRequirementId;

    const backTo = (location.state as { from?: string } | null)?.from === 'home' ? '/' : `/programs/${programId}`;
    const [error, setError] = useState<string | undefined>(undefined);
    const [userCourses, setUserCourses] = useState<CourseOption[]>(initialUserCourses ?? []);
    const [optimisticFavorites, setOptimisticFavorites] = useState<Map<string, boolean>>(new Map());
    const hasChangedRef = useRef(false);
    const hasInitialUserCoursesRef = useRef(initialUserCourses !== undefined);

    const { isLoggedIn, authLoading, user } = useAuth();
    const { openLoginModal } = useModal();

    const sliceVersion = useSyncExternalStore(programSlices.subscribe, programSlices.getVersion);
    void sliceVersion; // 订阅版本号驱动重渲染,快照在渲染期派生

    const snap = programId ? programSlices.getSnapshot(programId) : undefined;
    const program = snap?.status === 'loaded' ? snap.program : undefined;
    const requirement = program?.requirements.find(r => r.info.id === requirementId);
    const sliceStatus = requirementId ? snap?.sliceStatus[requirementId] : undefined;
    const isSliceLoaded = sliceStatus === 'loaded';

    useEffect(() => {
        if (authLoading || !programId || !requirementId) return;
        ensureSliceCacheIdentity(user ? `user:${user.id}` : 'guest');
        programSlices.ensureProgram(programId);
        programSlices.prioritize(programId, [requirementId]);
    }, [authLoading, user, programId, requirementId]);

    // userCourses:overlay 由宿主页传入(纯缓存,零请求);独立路由首次拉取
    useEffect(() => {
        if (authLoading) return;
        if (!isLoggedIn) {
            setUserCourses([]);
            return;
        }
        if (hasInitialUserCoursesRef.current) return;
        userCoursesApi.getCourses().then(setUserCourses).catch(err => {
            console.error('Error fetching user courses:', err);
        });
    }, [authLoading, isLoggedIn]);

    // stateVersion 落地前的乱序保护:只应用最新一次发起的刷新结果
    //(stateVersion 在场时由编排器 applyFulfillment 内部兜底)
    const refreshSeqRef = useRef(0);

    // 改课后的轻量刷新:fulfillment patch 打在编排器的合并对象上,
    // overlay 场景下宿主页(同一编排器)同步更新。
    // outcome(契约 §4):内嵌详情档在场时直接用,省掉 GET fulfillment;
    // meta 在场且本 program 不受影响时只刷用户课程。
    const refreshFulfillment = async (outcome?: MutationEnvelope) => {
        if (!isLoggedIn || !programId) return;
        const seq = ++refreshSeqRef.current;
        try {
            const embedded = outcome?.fulfillments?.find(f => f.programId === programId);
            const affected = !outcome?.meta || outcome.meta.affectedProgramIds.includes(programId);
            const [courses, exams, fulfillment] = await Promise.all([
                userCoursesApi.getCourses(),
                userExamsApi.getExams(),
                embedded ?? (affected
                    ? programsApi.getProgramFulfillment(programId)
                    : Promise.resolve(null)),
            ]);
            if (seq !== refreshSeqRef.current) return;
            const userItems = [...courses, ...exams];
            // §3.4-c 双读对拍:userStates 覆盖全部用户课,任一详情档都可作基准
            const reference = fulfillment ?? outcome?.fulfillments?.[0];
            if (reference) reconcileUserItems(reference.userStates, userItems);
            if (fulfillment) {
                programSlices.applyFulfillment(programId, fulfillment, userItems);
            }
            // 内嵌档可能还带了其他已加载 program(编排器缓存共享),顺手打上
            outcome?.fulfillments?.forEach(f => {
                if (f.programId !== programId) programSlices.applyFulfillment(f.programId, f, userItems);
            });
            setUserCourses(courses);
        } catch (err) {
            if (seq !== refreshSeqRef.current) return;
            console.error('Error refreshing fulfillment:', err);
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };

    const { sidebarContainerRef, sidebarContentRef, containerStyle, contentClassName, contentStyle } = usePinnedSidebar({
        scrollContainerRef,
        deps: [requirement, isSliceLoaded, scrollContainerRef],
    });

    const handleBack = () => {
        if (isOverlay) {
            if (hasChangedRef.current) onDataChanged?.();
            onClose!();
        } else {
            navigate(backTo);
        }
    };

    const markChanged = () => { if (isOverlay) hasChangedRef.current = true; };

    const courseActions = useCourseActions({
        isLoggedIn,
        openLoginModal,
        userCourses,
        optimisticFavorites,
        setOptimisticFavorites,
        onSuccess: async (outcome) => {
            markChanged();
            await refreshFulfillment(outcome);
        },
        fulfillmentProgramIds: () => programSlices.getLoadedProgramIds(),
        onError: (err, action) => {
            if (action === 'favorite') {
                if (axios.isAxiosError(err) && err.response?.status === 401) {
                    setError('Please log in to save favorites');
                } else {
                    setError(err instanceof Error ? err.message : 'Failed to update favorite status');
                }
            } else {
                setError(err instanceof Error ? err.message : 'An error occurred');
            }
        },
        rethrow: true,
    });

    // 刷新由 onSuccess(refreshFulfillment)承担,组件只需 Promise<void>,吞掉 outcome
    const handleAddCourse = async (
        course: AddableCourse,
        overrides: CourseRequirementOverride[],
        semester: string | null,
        classSectionIds?: number[],
    ) => {
        setError(undefined);
        await courseActions.handleAddCourse(course, overrides, semester, classSectionIds);
    };

    const handleDeleteCourse = async (course: CourseLite) => {
        setError(undefined);
        await courseActions.handleDeleteCourse(course);
    };

    const handleFavoriteCourse = courseActions.handleFavoriteCourse;

    const handleOverride = async (course: CourseLite, override: CourseRequirementOverride) => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }

        const userItemId = course.userItemId;
        if (!userItemId) return;

        setError(undefined);
        try {
            const fulfillmentIds = programSlices.getLoadedProgramIds();
            const outcome = course.type === 'EXAM'
                ? await userExamsApi.addOverride(userItemId, override, fulfillmentIds)
                : await userCoursesApi.addOverride(userItemId, override, fulfillmentIds);
            markChanged();
            await refreshFulfillment(outcome);
        } catch (error) {
            console.error('Error updating override:', error);
            setError(error instanceof Error ? error.message : 'An error occurred');
            throw error;
        }
    };

    const skeletonError = snap?.status === 'error' ? (snap.error ?? 'Failed to load') : undefined;
    const notFound = program !== undefined && requirement === undefined;

    if (!snap || snap.status === 'loading') {
        return (
            <div className="mt-2 relative">
                <RequirementPageSkeleton />
            </div>
        );
    }

    if (skeletonError || notFound) {
        return (
            <div className="mt-2 relative">
                <div className="w-full max-w-[1200px] mx-auto px-4 md:px-12 pt-4">
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {notFound ? 'Requirement not found' : skeletonError}
                    </div>
                    {!notFound && (
                        <button
                            className="mt-3 text-sm text-red-700 underline"
                            onClick={() => programSlices.ensureProgram(programId!)}
                        >
                            Try again
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="mt-2 relative">
            {error && (
                <div className="w-full max-w-[1200px] mx-auto px-4 md:px-12 pt-4">
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                </div>
            )}
            {sliceStatus === 'error' && (
                <div className="w-full max-w-[1200px] mx-auto px-4 md:px-12 pt-4">
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-baseline justify-between gap-3">
                        <span>Failed to load courses for this requirement.</span>
                        <button
                            className="underline whitespace-nowrap"
                            onClick={() => programSlices.requestSlices(programId!, [requirementId!])}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            )}
            {program && requirement && (
                <div className="w-full max-w-[1200px] mx-auto px-4 md:px-12 grid grid-cols-1 md:grid-cols-[1fr_312px] gap-6">
                    <div className="min-w-0 pb-20">
                        <div className="mt-4 md:mt-3 mb-3">
                            <div className="flex items-center gap-1 md:gap-2 mb-2">
                                <button
                                    onClick={handleBack}
                                    className="p-1.5 -ml-2 md:-ml-14 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                                >
                                    <ChevronIcon direction="left" className="w-6 h-6 md:w-7 md:h-7 text-gray-600" />
                                </button>
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                        <span className="text-[12px] font-bold text-gray-600">
                                            {program.info.id.substring(0, 2).toUpperCase()}
                                        </span>
                                    </div>
                                    <span className="text-xs md:text-sm font-medium text-gray-700">
                                        {program.info.name}
                                    </span>
                                </div>
                            </div>
                            <h1 className="text-lg md:text-3xl font-bold text-gray-900 leading-tight">
                                {requirement.info.name} Requirement
                            </h1>
                        </div>
                        <div className="text-sm text-gray-500 flex flex-col gap-2">
                            {requirement.info.description.map((line) => (
                                <p key={line} className="text-sm leading-6 text-gray-600">
                                    {line}
                                </p>
                            ))}
                        </div>

                        {requirement.info.needVerification && (
                            <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                                <Attention className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-amber-800">Manual verification required</p>
                                    <p className="text-xs text-amber-700 mt-0.5">
                                        {getBroadKind(requirement) === "intensional"
                                            ? "Course matching for this requirement is simplified and may include courses that don't count. Please double-check with official Cornell resources."
                                            : "This requirement has complex rules. Please verify with official Cornell resources."}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="pt-4">
                            <RequirementDetailSection
                                programInfo={program.info}
                                requirement={requirement}
                                coursesMap={program.courses}
                                isSliceLoaded={isSliceLoaded}
                                handleAddCourse={handleAddCourse}
                                handleDeleteCourse={handleDeleteCourse}
                                handleFavoriteCourse={handleFavoriteCourse}
                                optimisticFavorites={optimisticFavorites}
                                userCourses={userCourses}
                            />
                        </div>
                    </div>
                    <div
                        ref={sidebarContainerRef}
                        className="hidden md:block self-start mt-2 md:mt-3"
                        style={containerStyle}
                    >
                        <div
                            ref={sidebarContentRef}
                            className={contentClassName}
                            style={contentStyle}
                        >
                            <RequirementInfoSidebar
                                program={program.info}
                                requirement={requirement}
                                coursesMap={program.courses}
                                isSliceLoaded={isSliceLoaded}
                                handleAddCourse={handleAddCourse}
                                handleDeleteCourse={handleDeleteCourse}
                                handleOverride={handleOverride}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
